/**
 * Leumit iframe postMessage — SessionEndResponse contract and parent messaging.
 * Origin must come from VITE_ALLOWED_LEUMIT_PARENT_ORIGIN (never '*').
 * Never log the raw JWT.
 */

import type { ResultStatus } from 'types/imageRecognition';
import type { SelfReportedResult } from 'types/models';
import type { TestType } from 'types/testType';

export enum SessionResultStatus {
  NEGATIVE = 10,
  POSITIVE = 20,
  INVALID = 40,
}

export type SessionResultLabel = 'negative' | 'positive' | 'invalid';

const ALLOWED_PARENT_ORIGIN = (import.meta.env.VITE_ALLOWED_LEUMIT_PARENT_ORIGIN || '').trim();

export const SESSION_RESULT_LABEL_MAP: Record<SessionResultStatus, SessionResultLabel> = {
  [SessionResultStatus.NEGATIVE]: 'negative',
  [SessionResultStatus.POSITIVE]: 'positive',
  [SessionResultStatus.INVALID]: 'invalid',
};

export interface SessionEndResponse {
  testType: TestType;
  token: string;
  result: SessionResultLabel;
  uniqueId: string;
  testExpireDate: string;
  status: SessionResultStatus;
}

export interface ResolvedSessionResult {
  status: SessionResultStatus;
  result: SessionResultLabel;
  isExpired: boolean;
}

const normalizeModelResult = (modelResult: ResultStatus | null | undefined): SessionResultLabel | null => {
  if (!modelResult) {
    return null;
  }

  switch (modelResult) {
    case 'POSITIVE':
      return 'positive';
    case 'NEGATIVE':
      return 'negative';
    case 'INVALID':
    case 'REJECTED':
    case 'MODEL_ERROR':
      return 'invalid';
    default:
      return 'invalid';
  }
};

const normalizeHumanResult = (humanResult: SelfReportedResult): SessionResultLabel | null => {
  if (humanResult === 'positive') {
    return 'positive';
  }
  if (humanResult === 'negative') {
    return 'negative';
  }
  // 'error' self-report maps to invalid
  return 'invalid';
};

/**
 * Returns true when testExpireDate is a parseable ISO datetime in the past.
 * Missing/blank/unparseable values are NOT treated as expired (caller decides separately).
 */
export const isTestExpireDateExpired = (testExpireDate: string): boolean => {
  if (!testExpireDate.trim()) {
    return false;
  }

  const expireMs = Date.parse(testExpireDate);
  if (Number.isNaN(expireMs)) {
    return false;
  }

  return expireMs < Date.now();
};

/**
 * Resolves the SessionEndResponse result/status from model + human self-report.
 * Both must AGREE for positive/negative; mismatch, invalid model, or expired date -> 40.
 * Missing/unparseable testExpireDate -> invalid without isExpired (no expired-toast UX).
 */
export const resolveSessionResult = (
  modelResult: ResultStatus | null | undefined,
  humanResult: SelfReportedResult,
  testExpireDate: string,
): ResolvedSessionResult => {
  const hasExpireDate = Boolean(testExpireDate.trim()) && !Number.isNaN(Date.parse(testExpireDate));
  if (!hasExpireDate) {
    return {
      status: SessionResultStatus.INVALID,
      result: SESSION_RESULT_LABEL_MAP[SessionResultStatus.INVALID],
      isExpired: false,
    };
  }

  const isExpired = isTestExpireDateExpired(testExpireDate);
  if (isExpired) {
    return {
      status: SessionResultStatus.INVALID,
      result: SESSION_RESULT_LABEL_MAP[SessionResultStatus.INVALID],
      isExpired: true,
    };
  }

  const modelLabel = normalizeModelResult(modelResult);
  const humanLabel = normalizeHumanResult(humanResult);

  // Invalid model (or unmapped) always wins
  if (modelLabel === null || modelLabel === 'invalid' || humanLabel === 'invalid') {
    return {
      status: SessionResultStatus.INVALID,
      result: SESSION_RESULT_LABEL_MAP[SessionResultStatus.INVALID],
      isExpired: false,
    };
  }

  // Mismatch -> invalid
  if (modelLabel !== humanLabel) {
    return {
      status: SessionResultStatus.INVALID,
      result: SESSION_RESULT_LABEL_MAP[SessionResultStatus.INVALID],
      isExpired: false,
    };
  }

  if (modelLabel === 'positive') {
    return {
      status: SessionResultStatus.POSITIVE,
      result: SESSION_RESULT_LABEL_MAP[SessionResultStatus.POSITIVE],
      isExpired: false,
    };
  }

  return {
    status: SessionResultStatus.NEGATIVE,
    result: SESSION_RESULT_LABEL_MAP[SessionResultStatus.NEGATIVE],
    isExpired: false,
  };
};

/**
 * Posts SessionEndResponse to the Leumit parent iframe.
 * Skips when not embedded or when targetOrigin is missing/invalid.
 */
export const sendSessionEndToParent = (payload: SessionEndResponse): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Not embedded in a parent frame
  if (window.parent === window) {
    return false;
  }

  if (!ALLOWED_PARENT_ORIGIN || ALLOWED_PARENT_ORIGIN === '*') {
    console.warn('[leumitPostMessage] No allowed parent origin configured; skipping postMessage');
    return false;
  }

  try {
    window.parent.postMessage(payload, ALLOWED_PARENT_ORIGIN);
    return true;
  } catch {
    console.warn('[leumitPostMessage] Failed to post SessionEndResponse to parent');
    return false;
  }
};
